import { Stack } from 'component-library';
import DOMPurify from 'dompurify';

import { useSelectedRecommendationCard } from '@/components/CoPilot/CoPilotContextProvider';
import { ButtonWidget } from '@/components/widgets/a/Button/Button';
import { IconButtonWidget } from '@/components/widgets/a/Button/IconButton';
import { CardLayout } from '@/components/widgets/a/Card/Card';
import { getStylesByCardType } from '@/components/widgets/a/Card/utils/getStylesByCardType';
import { PillWidget } from '@/components/widgets/a/Pill/Pill';
import { Body, Footer, Header } from '@/components/widgets/a/Text/Text';
import { RecommendationCard } from '@/components/widgets/o/RecommendationCard/types';

import { RecommendationActionButton } from './RecommendationActionButton';

export const ExpandedRecommendationCard = ({ recommendationCard }: { recommendationCard: RecommendationCard }) => {
  const selectRecommendationCard = useSelectedRecommendationCard();

  const { header, body, footer, type, pill, styles } = recommendationCard;

  const { text, i18nPath, planning, isDismisable, header: expandedHeader } = body?.expanded || {};

  const handleBackToCoPilotList = () => {
    selectRecommendationCard?.(null);
  };

  const cardStyles = styles ?? getStylesByCardType(type);
  const expandedHeaderText = expandedHeader || header?.text;

  return (
    <CardLayout>
      <IconButtonWidget
        icon='ri-arrow-left-s-line'
        variant='text'
        size='large'
        className='px-1 py-0.5 w-fit'
        onClick={handleBackToCoPilotList}
      />
      {pill && <PillWidget styles={cardStyles} {...pill} />}
      {expandedHeaderText && (
        <Header text={expandedHeaderText} className={header?.withColor ? `text-[${cardStyles.color}]` : ''} />
      )}
      {(text || i18nPath) && <Body text={text} i18nPath={i18nPath} />}
      {footer && (
        <Footer>
          {footer.text && <span className='font-semibold'>{footer.text}: </span>}
          {footer.value && (
            <span className={cardStyles.color ? `text-[${cardStyles.color}]` : 'text-black-100'}>{footer.value}</span>
          )}
        </Footer>
      )}
      {planning && planning.length > 0 && (
        <Stack spacing={4} className='bg-neutral-black-4 rounded-2xl p-4'>
          {planning.map((plan, index) => (
            <Stack key={`plan-${index}`} spacing={4}>
              {plan?.title && <Header text={plan.title} className='typography-body2Semibold' />}
              {plan?.text && <Body className='typography-body2 whitespace-pre-wrap' text={plan.text} />}
              {plan?.footer && (
                <Footer className='flex flex-col gap-1'>
                  {plan?.footer?.text && (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(plan?.footer?.text),
                      }}
                    />
                  )}
                  {plan?.footer?.value && (
                    <span className={cardStyles.color ? `text-[${cardStyles.color}]` : 'text-black-100'}>
                      {plan?.footer?.value}
                    </span>
                  )}
                </Footer>
              )}
            </Stack>
          ))}
        </Stack>
      )}
      <Stack spacing={2}>
        <RecommendationActionButton recommendationCard={recommendationCard} />
        {isDismisable && (
          <ButtonWidget variant='outline' i18nPath='global.widget.recommendationCard.button.notRelevant' />
        )}
      </Stack>
    </CardLayout>
  );
};
